import React from 'react'
import { escapeRegExp, isEmpty } from 'lodash'

import { fullNameOf } from './useOrgUsers'

// Building the alternation regex (map → filter → sort → escape → join →
// compile) over the whole org user list is the expensive part, and it only
// depends on orgUsers — not on the per-note text. Cache the compiled regex
// keyed by the orgUsers array reference (stable across renders from the
// useOrgUsers hook / redux) so it's built once per user list instead of once
// per note per render. `false` marks "no matchable names" so we don't recompute.
const regexCache = new WeakMap()

const mentionRegexFor = (orgUsers) => {
  if (regexCache.has(orgUsers)) {
    return regexCache.get(orgUsers)
  }
  const names = orgUsers.map(fullNameOf).filter(Boolean)
  const regex = !isEmpty(names)
    ? new RegExp(
        `@(${names
          .slice()
          .sort((a, b) => b.length - a.length)
          .map(escapeRegExp)
          .join('|')})`,
        'g'
      )
    : false
  regexCache.set(orgUsers, regex)
  return regex
}

/**
 * Returns an array of strings and <span> elements representing the note
 * body with `@FullName` substrings replaced by highlighted spans. Names
 * are sorted longest-first so "@First Last" wins over "@First" when both
 * exist in the user list.
 *
 * When orgUsers is empty (still loading or fetch failed) we just return
 * the text as-is — better than highlighting nothing or applying a fuzzy
 * regex that could catch a literal "@thing" that isn't a real mention.
 *
 * mentionClassName lets callers swap the chip class. MentionableTextarea
 * uses this to render an "invisible" backdrop chip that's just a colored
 * background — the textarea on top paints the actual @text.
 */
export const renderMentions = (text, orgUsers, mentionClassName = 'note-mention') => {
  if (!text) {
    return text
  }
  if (!orgUsers || isEmpty(orgUsers)) {
    return text
  }

  const regex = mentionRegexFor(orgUsers)
  if (!regex) {
    return text
  }
  // The cached regex is stateful (/g); reset before each walk.
  regex.lastIndex = 0

  const parts = []
  let lastIndex = 0
  let key = 0
  let match = regex.exec(text)
  while (match !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    parts.push(
      <span key={`m-${key}`} className={mentionClassName}>
        @{match[1]}
      </span>
    )
    key += 1
    lastIndex = match.index + match[0].length
    match = regex.exec(text)
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }
  return parts
}
